<template>
    <dialog ref="mailDialog" class="send-mail-dialog">
        <h3>{{ $t('sendTranscriptTitle') }}</h3>

        <div class="form-group">
            <label for="email">{{ $t('email') }}</label>
            <div class="email-chip-container">
                <div class="email-chips">
                    <div v-for="(email, index) in emails" :key="index" class="email-chip">
                        <span>{{ email }}</span>
                        <button type="button" class="chip-remove" @click="removeEmail(index)">×</button>
                    </div>
                    <input type="email" id="email" v-model="currentEmail" class="email-input" @keypress.enter="addEmail"
                        @blur="addEmail" :placeholder="$t('emailPlaceholder')" />
                </div>
            </div>
        </div>

        <div class="form-group">
            <label for="language">{{ $t('translation') }}</label>
            <select id="language" v-model="targetLanguage" class="form-control">
                <option v-for="lang in availableLanguages" :key="lang.code" :value="lang.code">
                    {{ $t(lang.name) }}
                </option>
            </select>
        </div>

        <div class="dialog-buttons">
            <button @click="sendMail" class="dialog-btn send-btn" :disabled="!isEmailListValid || isSending">
                {{ isSending ? $t('sending') : $t('send') }}
            </button>

            <button @click="close" class="dialog-btn cancel-btn">
                {{ $t('cancel') }}
            </button>
        </div>
    </dialog>
</template>

<script>
import { LANGUAGES_TRANSLATE } from '@/constants/language'
import { useSendMailService } from '@/services/sendMailService';

export default {
    name: 'SendMailDialog',
    props: {
        roomId: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            emails: [],
            currentEmail: '',
            targetLanguage: 'en',
            isSending: false,
            availableLanguages: LANGUAGES_TRANSLATE
        }
    },

    computed: {
        isEmailListValid() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return this.emails.length > 0 && this.emails.every(email => emailRegex.test(email))
        }
    },

    methods: {
        open() {
            this.$refs.mailDialog.showModal()
        },

        addEmail() {
            const email = this.currentEmail.trim()
            if (email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                if (emailRegex.test(email) && !this.emails.includes(email)) {
                    this.emails.push(email)
                    this.currentEmail = ''
                }
            }
        },

        removeEmail(index) {
            this.emails.splice(index, 1)
        },

        async sendMail() {
            if (!this.isEmailListValid) return

            try {
                this.isSending = true
                await useSendMailService({
                    received_user_emails: this.emails,
                    room_id: this.roomId,
                    target_language: this.targetLanguage.toUpperCase()
                })

                this.$emit('sent-successfully')
                this.resetForm()
                this.close()
            } catch (error) {
                this.$emit('send-error', error)
            } finally {
                this.isSending = false
            }
        },

        close() {
            this.$refs.mailDialog.close()
            this.$emit('close')
        },

        resetForm() {
            this.emails = []
            this.currentEmail = ''
            this.targetLanguage = 'en'
        }
    }
}
</script>

<style scoped>
.send-mail-dialog {
    font-family: 'Montserrat';
    background-color: var(--container-color);
    border-radius: 8px;
    width: 85%;
    max-width: 500px;
    padding: 5%;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-color);
    color: var(--text-color);
}

.send-mail-dialog::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
}

h3 {
    margin-top: 0;
    color: var(--text-color);
    margin-bottom: 24px;
}

.form-group {
    margin-bottom: 16px;
}

label {
    display: block;
    margin-bottom: 8px;
    color: var(--text-color);
}

.form-control {
    font-family: 'Montserrat';
    width: 100%;
    background-color: var(--container-color);
    border: 1px solid var(--border-color);
    color: var(--text-color);
    padding: 12px 16px;
    border-radius: 4px;
    font-size: 14px;
}

.form-control:focus {
    outline: none;
    border-color: #0ccdb8;
}

.email-chip-container {
    width: 100%;
}

.email-chips {
    font-family: 'Montserrat';
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    background-color: var(--container-color);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    padding: 8px;
    min-height: 40px;
    align-items: center;
}

.email-chip {
    display: flex;
    align-items: center;
    background-color: #0ccdb8;
    color: var(--text-color);
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 14px;
    gap: 4px;
}

.chip-remove {
    background: none;
    border: none;
    color: var(--text-color);
    font-size: 16px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
}

.email-input {
    font-family: 'Montserrat';
    flex: 1;
    background: none;
    border: none;
    color: var(--text-color);
    font-size: 14px;
    outline: none;
    padding: 4px 8px;
    min-width: 200px;
}

.email-input::placeholder {
    color: var(--text-color);
}

.dialog-buttons {
    font-family: 'Montserrat';
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
    gap: 12px;
}

.dialog-btn {
    font-family: 'Montserrat';
    padding: 10px 20px;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    min-width: 100px;
    transition: background-color 0.2s;
}

.send-btn {
    background-color: #0ccdb8;
    color: var(--text-color);
}

.send-btn:hover:not(:disabled) {
    background-color: #0fb3a2;
}

.send-btn:disabled {
    background-color: #bdc2c1;
    color: var(--text-color);
    cursor: not-allowed;
}

.cancel-btn {
    background-color: transparent;
    border: 1px solid var(--border-color);
    color: var(--text-color);
}

.cancel-btn:hover {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>